/**
 * Global notification handler for real-time notifications.
 *
 * A SINGLE generic handler (no per-event-type allowlist): it fires for *any*
 * notification received over the websocket and renders the focus-adaptive
 * "alert me in real time on this computer" surface:
 * - Focused tab    → in-app toast (the `in_app` channel)
 * - Unfocused tab  → browser/OS desktop pop-up (the `desktop` channel)
 * - Capacitor      → native local notification for background awareness
 * - Bell ledger    → always updated, but as a SEPARATE concern (SystemNotificationsBell)
 *
 * Whether a desktop pop-up is shown is governed by the F2 payload contract: the
 * backend surfaces the enabled `channels` per recipient+event, and we pop up only
 * when `'desktop'` is among them (focus permitting). Direct `notify.*` helper
 * paths leave `channels` unset → legacy rule: WS receipt implies `desktop`-eligible.
 *
 * React Query cache-invalidation is not done here at all. It used to be — a
 * static map of notification types that kept the AppBar surfaces (messaging
 * unread summary, agent-approval inbox and badge) fresh. `DataFreshnessProvider`
 * now invalidates every mounted query in a module the moment a write lands
 * there, whoever made it, so those entries were redundant and are gone
 * (`docs/platform/data-freshness-design.md` § What this replaces).
 *
 * Mount this component once at the app level, inside both NotificationProvider
 * and RealtimeNotificationProvider.
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useRealtimeNotifications } from './NotificationContext';
import { useNotification } from '../context/NotificationContext';
import { useAuth } from '../auth/AuthContext';
import { apiGet } from '../api/axiosInstance';
import { entityUrlContext, getEntityUrl } from '../utils/entityUrls';
import { applyNotificationDeepLinkTab } from './deepLinkTab';
import { notificationActionUrl } from './actionUrl';
import { localizedNotificationTitle } from './notificationTitle';
import {
  initNativeNotifications,
  showNativeNotification,
  onNativeNotificationTap,
  isNativeNotificationsAvailable,
} from './nativeNotifications';
import { playNotificationSound } from './notificationSound';
import { usePushNotifications } from './usePushNotifications';
import type { Notification, NotificationChannel } from './types';

/**
 * Request browser notification permission if not already granted.
 * Returns true if permission is granted.
 */
async function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) {
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission === 'denied') {
    return false;
  }

  const permission = await Notification.requestPermission();
  return permission === 'granted';
}

/**
 * Strip HTML tags from a string to produce plain text.
 * Used for browser/native notifications which don't render HTML.
 */
function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.innerText || doc.body.textContent || '').replace(/\s+/g, ' ').trim();
}

/**
 * How much of a notification body a toast carries — about two lines at the toast's
 * width. A toast is a glance, and sonner grows to hold whatever it is given, so an
 * unbounded body turns a passing alert into a paragraph parked over the page.
 */
const TOAST_DESCRIPTION_MAX = 140;

/**
 * The notification body as a toast's second line: plain text, one line's worth of
 * whitespace, cut at a word boundary.
 *
 * Returns `undefined` for an empty body so the caller can pass the result straight
 * through — sonner renders no second line for undefined, and an empty string would
 * reserve the space for nothing.
 *
 * Exported for its test: the cut is the whole behaviour here, and it is invisible
 * from outside — a component test asserting the toast fired passes at any length.
 */
export function toastDescription(body: string): string | undefined {
  const text = stripHtml(body);
  if (!text) return undefined;
  if (text.length <= TOAST_DESCRIPTION_MAX) return text;
  const cut = text.slice(0, TOAST_DESCRIPTION_MAX);
  // Back up to the last space so the cut never lands mid-word — unless the whole
  // window is one unbroken token (a URL, a scanned reference), where backing up
  // would throw the line away.
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

/**
 * Show a browser desktop notification.
 */
function showDesktopNotification(
  title: string,
  body: string,
  tagId: string,
  onClick?: () => void
): void {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }

  const notification = new Notification(title, {
    body: stripHtml(body),
    icon: '/favicon.ico',
    tag: `panluma-notif-${tagId}`, // Unique tag per notification (de-dupes repeats)
  });

  if (onClick) {
    notification.onclick = () => {
      window.focus();
      onClick();
      notification.close();
    };
  }

  // Auto-close after 5 seconds
  setTimeout(() => notification.close(), 5000);
}

/**
 * Desktop pop-up eligibility from the F2 `channels` contract.
 * `null`/undefined channels = legacy direct-`notify.*` path → WS receipt implies
 * the notification is `desktop`-eligible (preserves pre-refactor behavior).
 */
function isDesktopEligible(channels?: NotificationChannel[] | null): boolean {
  if (channels == null) return true;
  return channels.includes('desktop');
}

/**
 * In-app (toast/bell) eligibility from the F2 `channels` contract.
 * `null`/undefined channels = legacy direct-`notify.*` path → `in_app`-eligible.
 */
function isInAppEligible(channels?: NotificationChannel[] | null): boolean {
  if (channels == null) return true;
  return channels.includes('in_app');
}

/**
 * True when the current location is already showing the notification's entity,
 * so we should not interrupt the user with a toast/pop-up. Compares the deep-link
 * path and requires every query param of the target to match the current URL.
 */
function isViewingEntity(
  targetUrl: string,
  location: { pathname: string; search: string },
): boolean {
  const [targetPath, targetQuery] = targetUrl.split('?');
  if (location.pathname !== targetPath) return false;
  if (!targetQuery) return true;
  const target = new URLSearchParams(targetQuery);
  const current = new URLSearchParams(location.search);
  for (const [key, value] of target) {
    if (current.get(key) !== value) return false;
  }
  return true;
}

/**
 * Subset of the user's central notification settings needed for sound control.
 *
 * The sound switch is global and lives on `user_notification_settings`. It used
 * to be read from `/api/v1/messaging/settings/user`, which meant one module's
 * settings row silently governed the notification sound for the whole app.
 */
interface NotificationSoundSettings {
  sound_enabled: boolean;
}

export function GlobalNotificationHandler() {
  const { subscribe, isConnected } = useRealtimeNotifications();
  const { showInfo } = useNotification();
  const { t } = useTranslation(['notifications']);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const hasRequestedPermission = useRef(false);

  // Fetch the user's central notification settings to check sound_enabled
  const { data: notificationSettings } = useQuery({
    queryKey: ['notification-settings'],
    queryFn: () =>
      apiGet<NotificationSoundSettings>('/api/v1/user-management/settings/notifications'),
    staleTime: 5 * 60 * 1000, // 5 min — settings rarely change
    // Settings-factory routes are `require_auth`, which 401s a tenant-less
    // principal (a `pending_setup` user in the Setup Wizard). Sound falls back
    // to enabled, which is what a user with no settings row gets anyway.
    enabled: !!user?.tenant_id,
  });
  const soundEnabledRef = useRef(true);
  useEffect(() => {
    soundEnabledRef.current = notificationSettings?.sound_enabled ?? true;
  }, [notificationSettings?.sound_enabled]);

  // Initialize FCM push notifications on Capacitor (Android/iOS)
  usePushNotifications();

  // Use refs to avoid stale closures in the (stable) notification handler
  const userIdRef = useRef(user?.id);
  const locationRef = useRef(location);
  const showInfoRef = useRef(showInfo);
  const navigateRef = useRef(navigate);
  const tRef = useRef(t);

  // Keep refs in sync
  useEffect(() => {
    userIdRef.current = user?.id;
  }, [user?.id]);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    showInfoRef.current = showInfo;
  }, [showInfo]);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  // Request notification permission on mount (browser or native)
  useEffect(() => {
    if (isConnected && !hasRequestedPermission.current) {
      hasRequestedPermission.current = true;

      if (isNativeNotificationsAvailable()) {
        initNativeNotifications();
      } else if ('Notification' in window && Notification.permission === 'default') {
        // Note: Modern browsers may block this without user gesture
        requestNotificationPermission();
      }
    }
  }, [isConnected]);

  // Handle native notification taps (Capacitor) — navigate to the relevant page
  useEffect(() => {
    if (!isNativeNotificationsAvailable()) return;

    let cleanup: (() => void) | undefined;
    onNativeNotificationTap((data) => {
      if (data?.navigateTo) {
        navigateRef.current(data.navigateTo);
      }
    }).then((fn) => { cleanup = fn; });

    return () => { cleanup?.(); };
  }, []);

  // Generic alert handler: one subscription for ALL notification types.
  // Stable across renders (reads live values via refs).
  useEffect(() => {
    const handleNotification = (notification: Notification) => {
      // Never alert the actor about their own action — unless the backend has said this
      // is one of the events whose "actor" is not necessarily the person who acted. An
      // API key authenticates as its OWNER, so an automation transitions a record as the
      // human whose key it carries; `notify_actor` is that decision, resolved server-side
      // and carried on the payload the same way `channels` is. Without honouring it here
      // the backend opt-out keeps only the bell entry and the email, and the live alert —
      // toast, pop-up, sound — is dropped for exactly the recipient it was opted in for.
      if (!notification.notify_actor && notification.actor_user_id === userIdRef.current) return;

      // The toast/pop-up surface needs a human-readable title. Transient refresh
      // cues (entity.thread.updated, typing.indicator) carry none — skip them.
      if (!notification.title) return;
      // Stored titles are persisted in English; re-derive a localized title from
      // the catalog by notification_type (falls back to the stored title).
      const title = localizedNotificationTitle(tRef.current, notification.type, notification.title);
      const body = notification.body ?? '';
      const tagId = notification.entity_id || notification.id;

      // Entity identity via the Entity URL Registry (null if unresolvable). Used
      // for the "already viewing" check — tab query params are NOT part of the
      // identity, so it stays the canonical entity path.
      const entityUrl =
        notification.entity_type && notification.entity_id
          ? getEntityUrl({
              entityType: notification.entity_type,
              entityId: notification.entity_id,
            })
          : null;

      // Navigation target on click/tap — a SEPARATE resolution from the identity
      // above: it feeds `data` in as template context, so a nested entity lands on
      // its specific page (a chatbot channel → the owning bot's Channels tab)
      // rather than the generic list, matching notificationHref()
      // (SystemNotificationsBell), resolvePushNavigationUrl and the backend email
      // CTA. Some types also deep-link to a specific tab (ticket message →
      // Messages, comment mention → Comments); centralized in
      // applyNotificationDeepLinkTab.
      const navTarget =
        notification.entity_type && notification.entity_id
          ? getEntityUrl({
              entityType: notification.entity_type,
              entityId: notification.entity_id,
              context: entityUrlContext(notification.data),
            })
          : null;
      // An alert whose subject is not an addressable entity (a usage quota, an
      // expiring OAuth grant) carries no entity_type and links through
      // data.action_url instead — the same field the backend email CTA resolves.
      // Second, so an entity link always wins, and through the shared
      // notificationActionUrl guard the bell and mobile push use: navigate() on an
      // absolute or protocol-relative URL is an open redirect.
      const url = navTarget
        ? applyNotificationDeepLinkTab(navTarget, notification.type)
        : notificationActionUrl(notification.data);

      // Don't interrupt a user already looking at the entity; the bell still
      // updates via its own SystemNotificationsBell subscription.
      if (entityUrl && isViewingEntity(entityUrl, locationRef.current)) return;

      const native = isNativeNotificationsAvailable();
      const desktopEligible = isDesktopEligible(notification.channels);
      const inAppEligible = isInAppEligible(notification.channels);
      const backgrounded = document.hidden || !document.hasFocus();

      // Sound is interruptive — gate it like the desktop channel. Native handles
      // its own sound, so skip the web sound on Capacitor.
      if (desktopEligible && soundEnabledRef.current && !native) {
        playNotificationSound();
      }

      if (native) {
        // Mobile: native local notification for background awareness.
        if (desktopEligible) {
          showNativeNotification(
            title,
            stripHtml(body),
            `panluma-notif-${tagId}`,
            url ? { navigateTo: url } : {},
            soundEnabledRef.current,
          );
        }
        return;
      }

      if (backgrounded) {
        // Unfocused tab → OS pop-up (gated on the desktop channel).
        if (desktopEligible) {
          showDesktopNotification(
            title,
            body,
            tagId,
            url ? () => navigateRef.current(url) : undefined,
          );
        }
      } else if (inAppEligible) {
        // Focused tab → in-app toast, WITH the body under the title.
        //
        // The body is the per-event detail — the note on a due reminder, the
        // excerpt on a mention — and dropping it left the toast saying only what
        // KIND of thing happened. The bell popover and both pop-up branches above
        // have always shown it; the toast was the one surface that did not, so a
        // reminder arrived on screen as a bare "Reminder due" with the message the
        // user had written to themselves nowhere in sight.
        showInfoRef.current(title, toastDescription(body));
      } else if (desktopEligible) {
        // Edge case: desktop-only recipient on a focused tab still gets the pop-up.
        showDesktopNotification(
          title,
          body,
          tagId,
          url ? () => navigateRef.current(url) : undefined,
        );
      }
    };

    return subscribe('*', handleNotification);
  }, [subscribe]);

  // This component doesn't render anything
  return null;
}
